import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  alpha,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  Tabs,
  Tab,
  MenuItem,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  InputLabel,
  Select,
  Grid,
  Link,
  Tooltip,
  CircularProgress,
  InputAdornment,
  Autocomplete
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Campaign,
  Archive,
  Search,
  AttachFile,
  Image as ImageIcon,
  Description,
  OpenInNew,
  FilterList,
  Clear,
  Refresh,
  NotificationsActive,
  ManageAccounts
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import axios from 'axios';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { ROLES } from '../constants/roles';
import { LOGIN_DEPARTMENTS } from '../constants/loginDepartments';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const NOTICE_TYPES = ['Note', 'Reminder', 'Notice', 'Document', 'Image'];
const TARGET_DEPARTMENTS = LOGIN_DEPARTMENTS.filter((d) => d.slug !== 'client');

const THEME = {
  ink: '#0b1f33',
  navy: '#132a43',
  steel: '#3d5a80',
  mist: '#e8eef5',
  paper: '#f4f7fb',
  card: '#ffffff',
  border: '#d5dee9',
  gold: '#9a7b4f',
  goldSoft: '#c4a574',
  success: '#1b6b4a',
  warning: '#a16207',
  danger: '#9b2c2c',
  info: '#1d4f7c',
  muted: '#5c6b7a'
};

const TYPE_TONE = {
  Note: THEME.steel,
  Reminder: THEME.warning,
  Notice: THEME.info,
  Document: THEME.navy,
  Image: THEME.success
};

const deptName = (slug) => TARGET_DEPARTMENTS.find((d) => d.slug === slug)?.name || slug;

const emptyForm = {
  title: '',
  body: '',
  type: 'Notice',
  audience: 'everyone',
  targetDepartment: '',
  expiresAt: dayjs().add(7, 'day')
};

const authorLabel = (user) => {
  if (!user) return 'Unknown';
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return name || user.email || 'Unknown';
};

const isImageMime = (mime) => Boolean(mime && mime.startsWith('image/'));

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: THEME.card,
    borderRadius: 1.5,
    '& fieldset': { borderColor: THEME.border },
    '&:hover fieldset': { borderColor: alpha(THEME.steel, 0.55) },
    '&.Mui-focused fieldset': { borderColor: THEME.steel }
  },
  '& .MuiInputLabel-root.Mui-focused': { color: THEME.steel }
};

const SummaryMetric = ({ label, value, hint, tone = 'navy', icon }) => {
  const toneColor =
    tone === 'gold'
      ? THEME.gold
      : tone === 'success'
        ? THEME.success
        : tone === 'warning'
          ? THEME.warning
          : tone === 'info'
            ? THEME.info
            : THEME.navy;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        height: '100%',
        borderRadius: 2.5,
        border: `1px solid ${THEME.border}`,
        background: `linear-gradient(145deg, ${THEME.card} 0%, ${alpha(toneColor, 0.06)} 100%)`,
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 10px 28px ${alpha(THEME.ink, 0.08)}`
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: toneColor
        }
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Typography
          variant="overline"
          sx={{ color: THEME.muted, letterSpacing: 1.2, fontWeight: 600, lineHeight: 1.2 }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.25,
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(toneColor, 0.12),
            color: toneColor
          }}
        >
          {icon}
        </Box>
      </Stack>
      <Typography
        variant="h3"
        sx={{
          mt: 1,
          mb: 0.5,
          color: THEME.ink,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.02em'
        }}
      >
        {value}
      </Typography>
      <Typography variant="body2" sx={{ color: THEME.muted }}>
        {hint}
      </Typography>
    </Paper>
  );
};

const NoticeBoard = () => {
  const { user, isReadOnly } = useAuth();
  const { showToast } = useToast();
  const [view, setView] = useState('active');
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [postToDelete, setPostToDelete] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [permissions, setPermissions] = useState({ canPublish: false, canManagePublishers: false });
  const [publishersDialogOpen, setPublishersDialogOpen] = useState(false);
  const [publisherCandidates, setPublisherCandidates] = useState([]);
  const [selectedPublishers, setSelectedPublishers] = useState([]);
  const [publishersLoading, setPublishersLoading] = useState(false);
  const [publishersSaving, setPublishersSaving] = useState(false);

  const [filters, setFilters] = useState({
    type: '',
    author: '',
    audience: '',
    targetDepartment: '',
    search: '',
    from: null,
    to: null,
    sort: 'newest'
  });
  const [searchInput, setSearchInput] = useState('');

  const NOTICE_ADMIN_ROLES = useMemo(() => ([
    ROLES.SUPER_ADMIN,
    ROLES.ACCOUNTABLE_MANAGER,
    ROLES.AM_MRO,
    ROLES.OPERATIONS_MANAGER
  ]), []);

  const canPublish = !isReadOnly && permissions.canPublish;
  const canManagePublishers = !isReadOnly && permissions.canManagePublishers;

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((f) => (f.search === searchInput ? f : { ...f, search: searchInput }));
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const canManage = useCallback((post) => {
    if (!user || isReadOnly) return false;
    if (NOTICE_ADMIN_ROLES.includes(user.role)) return true;
    return String(post.createdBy?._id || post.createdBy) === String(user.id || user._id);
  }, [user, isReadOnly, NOTICE_ADMIN_ROLES]);

  const fetchAuthors = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/notice-board/authors`);
      setAuthors(response.data.authors || []);
    } catch (error) {
      console.error('Error fetching notice authors:', error);
    }
  }, []);

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        view,
        sort: filters.sort || 'newest'
      };
      if (filters.type) params.type = filters.type;
      if (filters.author) params.author = filters.author;
      if (filters.audience) params.audience = filters.audience;
      if (filters.targetDepartment) params.targetDepartment = filters.targetDepartment;
      if (filters.search.trim()) params.search = filters.search.trim();
      if (filters.from) params.from = dayjs(filters.from).startOf('day').toISOString();
      if (filters.to) params.to = dayjs(filters.to).endOf('day').toISOString();

      const response = await axios.get(`${API_URL}/notice-board`, { params });
      setPosts(response.data.posts || []);
      if (response.data.permissions) {
        setPermissions({
          canPublish: Boolean(response.data.permissions.canPublish),
          canManagePublishers: Boolean(response.data.permissions.canManagePublishers)
        });
      }
    } catch (error) {
      console.error('Error fetching notice board:', error);
      showToast(error.response?.data?.message || 'Failed to load notice board', 'error');
    } finally {
      setLoading(false);
    }
  }, [view, filters, showToast]);

  const openPublishersDialog = async () => {
    setPublishersDialogOpen(true);
    setPublishersLoading(true);
    try {
      const [publishersRes, candidatesRes] = await Promise.all([
        axios.get(`${API_URL}/notice-board/publishers`),
        axios.get(`${API_URL}/notice-board/publisher-candidates`)
      ]);
      const current = publishersRes.data.publishers || [];
      const candidates = candidatesRes.data.users || [];
      // Merge so currently selected people stay visible in the Autocomplete options
      const byId = new Map();
      [...candidates, ...current].forEach((u) => {
        if (u?._id) byId.set(String(u._id), u);
      });
      setPublisherCandidates([...byId.values()]);
      setSelectedPublishers(current);
    } catch (error) {
      console.error('Error loading publishers:', error);
      showToast(error.response?.data?.message || 'Failed to load publishers', 'error');
      setPublishersDialogOpen(false);
    } finally {
      setPublishersLoading(false);
    }
  };

  const handleSavePublishers = async () => {
    setPublishersSaving(true);
    try {
      const userIds = selectedPublishers.map((u) => u._id);
      const response = await axios.put(`${API_URL}/notice-board/publishers`, { userIds });
      setSelectedPublishers(response.data.publishers || []);
      showToast('Publisher list updated', 'success');
      setPublishersDialogOpen(false);
    } catch (error) {
      console.error('Error saving publishers:', error);
      showToast(error.response?.data?.message || 'Failed to update publishers', 'error');
    } finally {
      setPublishersSaving(false);
    }
  };

  useEffect(() => {
    fetchAuthors();
  }, [fetchAuthors]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleOpenAdd = () => {
    setSelectedPost(null);
    setFormData(emptyForm);
    setFiles([]);
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleOpenEdit = (post) => {
    setSelectedPost(post);
    setFormData({
      title: post.title || '',
      body: post.body || '',
      type: post.type || 'Notice',
      audience: post.audience === 'department' ? 'department' : 'everyone',
      targetDepartment: post.targetDepartment || '',
      expiresAt: post.expiresAt ? dayjs(post.expiresAt) : dayjs().add(7, 'day')
    });
    setFiles([]);
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleOpenDelete = (post) => {
    setPostToDelete(post);
    setDeleteDialogOpen(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.title.trim()) errors.title = 'Title is required';
    if (!formData.type) errors.type = 'Type is required';
    if (formData.audience === 'department' && !formData.targetDepartment) {
      errors.targetDepartment = 'Select a department';
    }
    if (!formData.expiresAt || !dayjs(formData.expiresAt).isValid()) {
      errors.expiresAt = 'Last day is required';
    } else if (dayjs(formData.expiresAt).endOf('day').isBefore(dayjs().startOf('day'))) {
      errors.expiresAt = 'Last day must be today or later';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const payload = new FormData();
      payload.append('title', formData.title.trim());
      payload.append('body', formData.body.trim());
      payload.append('type', formData.type);
      payload.append('audience', formData.audience);
      if (formData.audience === 'department') {
        payload.append('targetDepartment', formData.targetDepartment);
      }
      payload.append('expiresAt', dayjs(formData.expiresAt).format('YYYY-MM-DD'));
      files.forEach((file) => payload.append('attachments', file));

      if (selectedPost) {
        await axios.put(`${API_URL}/notice-board/${selectedPost._id}`, payload);
        showToast('Notice updated', 'success');
      } else {
        await axios.post(`${API_URL}/notice-board`, payload);
        showToast('Notice published', 'success');
      }
      setDialogOpen(false);
      setSelectedPost(null);
      setFiles([]);
      fetchAuthors();
      fetchPosts();
    } catch (error) {
      console.error('Error saving notice:', error);
      const apiErrors = error.response?.data?.errors;
      if (apiErrors?.length) {
        const mapped = {};
        apiErrors.forEach((e) => {
          if (e.path) mapped[e.path] = e.msg;
        });
        setFormErrors(mapped);
      }
      showToast(error.response?.data?.message || 'Failed to save notice', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`${API_URL}/notice-board/${postToDelete._id}`);
      showToast('Notice removed', 'success');
      setDeleteDialogOpen(false);
      setPostToDelete(null);
      fetchPosts();
    } catch (error) {
      console.error('Error deleting notice:', error);
      showToast(error.response?.data?.message || 'Failed to remove notice', 'error');
    }
  };

  const clearFilters = () => {
    setSearchInput('');
    setFilters({
      type: '',
      author: '',
      audience: '',
      targetDepartment: '',
      search: '',
      from: null,
      to: null,
      sort: 'newest'
    });
  };

  const hasActiveFilters = useMemo(() => (
    Boolean(
      filters.type
      || filters.author
      || filters.audience
      || filters.targetDepartment
      || filters.search.trim()
      || filters.from
      || filters.to
      || filters.sort !== 'newest'
    )
  ), [filters]);

  const stats = useMemo(() => ({ total: posts.length }), [posts]);

  const previewImage = (post) => {
    const img = (post.attachments || []).find((a) => isImageMime(a.mimeType));
    return img?.url || null;
  };

  const goldButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    bgcolor: THEME.gold,
    color: THEME.ink,
    boxShadow: 'none',
    px: 2.25,
    borderRadius: 1.5,
    '&:hover': { bgcolor: THEME.goldSoft, boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: alpha(THEME.gold, 0.4), color: alpha(THEME.ink, 0.5) }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          mx: { xs: -1, sm: -2 },
          mb: -2,
          px: { xs: 1.5, sm: 2.5 },
          pb: 3,
          pt: 0.5,
          minHeight: '100%',
          background: `
            radial-gradient(ellipse 80% 50% at 100% -10%, ${alpha(THEME.gold, 0.12)} 0%, transparent 55%),
            radial-gradient(ellipse 60% 40% at 0% 0%, ${alpha(THEME.steel, 0.1)} 0%, transparent 50%),
            linear-gradient(180deg, ${THEME.paper} 0%, #eef2f7 100%)
          `
        }}
      >
        {/* Hero */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${THEME.border}`,
            background: `linear-gradient(125deg, ${THEME.ink} 0%, ${THEME.navy} 48%, ${THEME.steel} 100%)`,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: `
                linear-gradient(90deg, transparent 60%, ${alpha(THEME.gold, 0.15)} 100%),
                repeating-linear-gradient(
                  -12deg,
                  transparent,
                  transparent 18px,
                  ${alpha('#ffffff', 0.015)} 18px,
                  ${alpha('#ffffff', 0.015)} 19px
                )
              `,
              pointerEvents: 'none'
            }
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
            sx={{ p: { xs: 2.5, md: 3 }, position: 'relative', zIndex: 1 }}
          >
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.25,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: alpha(THEME.gold, 0.2),
                    color: THEME.goldSoft,
                    border: `1px solid ${alpha(THEME.goldSoft, 0.35)}`
                  }}
                >
                  <Campaign fontSize="small" />
                </Box>
                <Chip
                  size="small"
                  label={view === 'active' ? 'Live board' : 'Archive'}
                  sx={{
                    bgcolor: alpha('#ffffff', 0.1),
                    color: alpha('#ffffff', 0.9),
                    border: `1px solid ${alpha('#ffffff', 0.18)}`,
                    fontWeight: 600,
                    letterSpacing: 0.4
                  }}
                />
              </Stack>
              <Typography
                variant="h4"
                sx={{ color: '#fff', fontWeight: 700, letterSpacing: '-0.02em', mb: 0.75 }}
              >
                Notice Board
              </Typography>
              <Typography variant="body2" sx={{ color: alpha('#ffffff', 0.72), maxWidth: 560 }}>
                Shared notes, reminders, notices and documents — for everyone or a specific department.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
                onClick={fetchPosts}
                disabled={loading}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  color: '#fff',
                  borderColor: alpha('#ffffff', 0.35),
                  borderRadius: 1.5,
                  '&:hover': {
                    borderColor: alpha('#ffffff', 0.6),
                    bgcolor: alpha('#ffffff', 0.08)
                  }
                }}
              >
                Refresh
              </Button>
              {canManagePublishers && (
                <Button
                  variant="outlined"
                  startIcon={<ManageAccounts />}
                  onClick={openPublishersDialog}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    color: '#fff',
                    borderColor: alpha('#ffffff', 0.35),
                    borderRadius: 1.5,
                    '&:hover': {
                      borderColor: alpha('#ffffff', 0.6),
                      bgcolor: alpha('#ffffff', 0.08)
                    }
                  }}
                >
                  Who can publish
                </Button>
              )}
              {canPublish && view === 'active' && (
                <Button variant="contained" startIcon={<Add />} onClick={handleOpenAdd} sx={goldButtonSx}>
                  Publish
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>

        {/* KPI strip */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SummaryMetric
              label={view === 'active' ? 'On the board' : 'In archive'}
              value={stats.total}
              hint={view === 'active' ? 'Visible notices right now' : 'Past last-day notices'}
              tone="navy"
              icon={<NotificationsActive fontSize="small" />}
            />
          </Grid>
        </Grid>

        {/* Tabs */}
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            borderRadius: 2.5,
            border: `1px solid ${THEME.border}`,
            bgcolor: THEME.card,
            overflow: 'hidden'
          }}
        >
          <Tabs
            value={view}
            onChange={(_, next) => setView(next)}
            sx={{
              minHeight: 52,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                color: THEME.muted,
                minHeight: 52
              },
              '& .Mui-selected': { color: `${THEME.ink} !important` },
              '& .MuiTabs-indicator': { bgcolor: THEME.gold, height: 3 }
            }}
          >
            <Tab value="active" icon={<Campaign fontSize="small" />} iconPosition="start" label="Board" />
            <Tab value="archive" icon={<Archive fontSize="small" />} iconPosition="start" label="Archive" />
          </Tabs>
        </Paper>

        {view === 'archive' && (
          <Paper
            elevation={0}
            sx={{
              mb: 2,
              p: 1.75,
              borderRadius: 2,
              border: `1px solid ${alpha(THEME.info, 0.28)}`,
              background: `linear-gradient(160deg, ${THEME.card} 0%, ${alpha(THEME.info, 0.07)} 100%)`
            }}
          >
            <Typography variant="body2" sx={{ color: THEME.info, fontWeight: 500 }}>
              Archive shows notices that have passed their last day. They leave the board automatically but stay here unless removed.
            </Typography>
          </Paper>
        )}

        {/* Filters */}
        <Paper
          elevation={0}
          sx={{
            p: 2.25,
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${THEME.border}`,
            bgcolor: THEME.card
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.75 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1,
                display: 'grid',
                placeItems: 'center',
                bgcolor: alpha(THEME.steel, 0.12),
                color: THEME.steel
              }}
            >
              <FilterList fontSize="small" />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: THEME.ink }}>
              Filters
            </Typography>
            {hasActiveFilters && (
              <Button
                size="small"
                startIcon={<Clear />}
                onClick={clearFilters}
                sx={{ textTransform: 'none', color: THEME.steel, fontWeight: 600 }}
              >
                Clear
              </Button>
            )}
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                sx={fieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" sx={{ color: THEME.muted }} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small" sx={fieldSx}>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={filters.type}
                  onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))}
                >
                  <MenuItem value="">All types</MenuItem>
                  {NOTICE_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small" sx={fieldSx}>
                <InputLabel>Author</InputLabel>
                <Select
                  label="Author"
                  value={filters.author}
                  onChange={(e) => setFilters((f) => ({ ...f, author: e.target.value }))}
                >
                  <MenuItem value="">Everyone</MenuItem>
                  {authors.map((a) => (
                    <MenuItem key={a._id} value={a._id}>{authorLabel(a)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small" sx={fieldSx}>
                <InputLabel>Audience</InputLabel>
                <Select
                  label="Audience"
                  value={filters.audience}
                  onChange={(e) => setFilters((f) => ({
                    ...f,
                    audience: e.target.value,
                    targetDepartment: e.target.value === 'department' ? f.targetDepartment : ''
                  }))}
                >
                  <MenuItem value="">All audiences</MenuItem>
                  <MenuItem value="everyone">Everyone</MenuItem>
                  <MenuItem value="department">Department</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small" disabled={filters.audience === 'everyone'} sx={fieldSx}>
                <InputLabel>Department</InputLabel>
                <Select
                  label="Department"
                  value={filters.targetDepartment}
                  onChange={(e) => setFilters((f) => ({
                    ...f,
                    targetDepartment: e.target.value,
                    audience: e.target.value ? 'department' : f.audience
                  }))}
                >
                  <MenuItem value="">Any department</MenuItem>
                  {TARGET_DEPARTMENTS.map((d) => (
                    <MenuItem key={d.slug} value={d.slug}>{d.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small" sx={fieldSx}>
                <InputLabel>Sort</InputLabel>
                <Select
                  label="Sort"
                  value={filters.sort}
                  onChange={(e) => setFilters((f) => ({ ...f, sort: e.target.value }))}
                >
                  <MenuItem value="newest">Newest first</MenuItem>
                  <MenuItem value="oldest">Oldest first</MenuItem>
                  <MenuItem value="expiry">Expiring soon</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={6} md={2}>
              <DatePicker
                label="From"
                value={filters.from}
                onChange={(v) => setFilters((f) => ({ ...f, from: v }))}
                slotProps={{ textField: { size: 'small', fullWidth: true, sx: fieldSx } }}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2}>
              <DatePicker
                label="To"
                value={filters.to}
                onChange={(v) => setFilters((f) => ({ ...f, to: v }))}
                slotProps={{ textField: { size: 'small', fullWidth: true, sx: fieldSx } }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Posts */}
        {loading ? (
          <Box sx={{ py: 10, display: 'grid', placeItems: 'center' }}>
            <CircularProgress sx={{ color: THEME.steel }} />
          </Box>
        ) : posts.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 5,
              textAlign: 'center',
              borderRadius: 2.5,
              border: `1px solid ${THEME.border}`,
              bgcolor: THEME.card
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                mx: 'auto',
                mb: 2,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                bgcolor: alpha(THEME.steel, 0.1),
                color: THEME.steel
              }}
            >
              <Campaign />
            </Box>
            <Typography variant="subtitle1" sx={{ color: THEME.ink, fontWeight: 700, mb: 0.5 }}>
              {view === 'active' ? 'No active notices' : 'Archive is empty'}
            </Typography>
            <Typography variant="body2" sx={{ color: THEME.muted }}>
              {view === 'active'
                ? 'Publish a note, reminder, document or image for the team.'
                : 'Expired notices will appear here after their last day.'}
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {posts.map((post) => {
              const imageUrl = previewImage(post);
              const typeTone = TYPE_TONE[post.type] || THEME.steel;
              return (
                <Grid item xs={12} md={6} lg={4} key={post._id}>
                  <Paper
                    elevation={0}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2.5,
                      border: `1px solid ${THEME.border}`,
                      background: `linear-gradient(145deg, ${THEME.card} 0%, ${alpha(typeTone, 0.05)} 100%)`,
                      overflow: 'hidden',
                      position: 'relative',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 10px 28px ${alpha(THEME.ink, 0.08)}`
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 4,
                        bgcolor: typeTone,
                        zIndex: 1
                      }
                    }}
                  >
                    {imageUrl && (
                      <Box
                        component="img"
                        src={imageUrl}
                        alt={post.title}
                        sx={{
                          width: '100%',
                          height: 160,
                          objectFit: 'cover',
                          borderBottom: `1px solid ${THEME.border}`
                        }}
                      />
                    )}
                    <Box sx={{ p: 2.25, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }} flexWrap="wrap" useFlexGap>
                        <Chip
                          size="small"
                          label={post.type}
                          sx={{
                            fontWeight: 600,
                            bgcolor: alpha(typeTone, 0.12),
                            color: typeTone,
                            border: `1px solid ${alpha(typeTone, 0.25)}`
                          }}
                        />
                        <Chip
                          size="small"
                          label={
                            post.audience === 'department' && post.targetDepartment
                              ? deptName(post.targetDepartment)
                              : 'Everyone'
                          }
                          sx={{
                            fontWeight: 600,
                            bgcolor: alpha(THEME.mist, 0.9),
                            color: THEME.navy,
                            border: `1px solid ${THEME.border}`
                          }}
                        />
                        <Typography variant="caption" sx={{ color: THEME.muted, fontWeight: 500 }}>
                          Until {dayjs(post.expiresAt).format('DD MMM YYYY')}
                        </Typography>
                      </Stack>

                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: THEME.ink,
                          fontWeight: 700,
                          letterSpacing: '-0.01em',
                          wordBreak: 'break-word',
                          mb: 0.75
                        }}
                      >
                        {post.title}
                      </Typography>

                      {post.body && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: THEME.muted,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            mb: 1.5,
                            maxHeight: 120,
                            overflow: 'hidden',
                            flexGrow: 1
                          }}
                        >
                          {post.body}
                        </Typography>
                      )}

                      {(post.attachments || []).length > 0 && (
                        <Stack spacing={0.5} sx={{ mb: 1 }}>
                          {post.attachments.map((att, idx) => (
                            <Link
                              key={`${att.url}-${idx}`}
                              href={att.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              variant="body2"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                color: THEME.info,
                                fontWeight: 500
                              }}
                            >
                              {isImageMime(att.mimeType) ? <ImageIcon fontSize="small" /> : <Description fontSize="small" />}
                              {att.originalName || att.filename || 'Attachment'}
                              <OpenInNew sx={{ fontSize: 14 }} />
                            </Link>
                          ))}
                        </Stack>
                      )}
                    </Box>

                    <Divider sx={{ borderColor: THEME.border }} />
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ px: 2, py: 1.25 }}
                    >
                      <Typography variant="caption" sx={{ color: THEME.muted }}>
                        {authorLabel(post.createdBy)} · {dayjs(post.createdAt).format('DD MMM YYYY HH:mm')}
                      </Typography>
                      {canManage(post) && (
                        <Stack direction="row" spacing={0.25}>
                          {view === 'active' && (
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEdit(post)}
                                sx={{ color: THEME.steel }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Remove">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDelete(post)}
                              sx={{ color: THEME.danger }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )}
                    </Stack>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}

        <Dialog
          open={dialogOpen}
          onClose={() => !saving && setDialogOpen(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: 2.5,
              border: `1px solid ${THEME.border}`
            }
          }}
        >
          <DialogTitle sx={{ color: THEME.ink, fontWeight: 700 }}>
            {selectedPost ? 'Edit notice' : 'Publish to notice board'}
          </DialogTitle>
          <DialogContent dividers sx={{ borderColor: THEME.border }}>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Title"
                required
                fullWidth
                value={formData.title}
                onChange={(e) => setFormData((f) => ({ ...f, title: e.target.value }))}
                error={Boolean(formErrors.title)}
                helperText={formErrors.title}
                sx={fieldSx}
              />
              <FormControl fullWidth required error={Boolean(formErrors.type)} sx={fieldSx}>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={formData.type}
                  onChange={(e) => setFormData((f) => ({ ...f, type: e.target.value }))}
                >
                  {NOTICE_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ color: THEME.muted, fontWeight: 600 }}>
                  Visible to
                </FormLabel>
                <RadioGroup
                  row
                  value={formData.audience}
                  onChange={(e) => setFormData((f) => ({
                    ...f,
                    audience: e.target.value,
                    targetDepartment: e.target.value === 'everyone' ? '' : f.targetDepartment
                  }))}
                >
                  <FormControlLabel
                    value="everyone"
                    control={<Radio sx={{ color: THEME.steel, '&.Mui-checked': { color: THEME.gold } }} />}
                    label="Everyone"
                  />
                  <FormControlLabel
                    value="department"
                    control={<Radio sx={{ color: THEME.steel, '&.Mui-checked': { color: THEME.gold } }} />}
                    label="One department"
                  />
                </RadioGroup>
              </FormControl>
              {formData.audience === 'department' && (
                <FormControl fullWidth required error={Boolean(formErrors.targetDepartment)} sx={fieldSx}>
                  <InputLabel>Department</InputLabel>
                  <Select
                    label="Department"
                    value={formData.targetDepartment}
                    onChange={(e) => setFormData((f) => ({ ...f, targetDepartment: e.target.value }))}
                  >
                    {TARGET_DEPARTMENTS.map((d) => (
                      <MenuItem key={d.slug} value={d.slug}>{d.name}</MenuItem>
                    ))}
                  </Select>
                  {formErrors.targetDepartment && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {formErrors.targetDepartment}
                    </Typography>
                  )}
                </FormControl>
              )}
              <TextField
                label="Notes / details"
                fullWidth
                multiline
                minRows={3}
                value={formData.body}
                onChange={(e) => setFormData((f) => ({ ...f, body: e.target.value }))}
                error={Boolean(formErrors.body)}
                helperText={formErrors.body}
                sx={fieldSx}
              />
              <DatePicker
                label="Last day on board"
                value={formData.expiresAt}
                minDate={dayjs().startOf('day')}
                onChange={(v) => setFormData((f) => ({ ...f, expiresAt: v }))}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: Boolean(formErrors.expiresAt),
                    helperText: formErrors.expiresAt || 'Removed from the board after this day; still available in Archive',
                    sx: fieldSx
                  }
                }}
              />
              <Button
                variant="outlined"
                component="label"
                startIcon={<AttachFile />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  color: THEME.steel,
                  borderColor: THEME.border,
                  borderRadius: 1.5,
                  '&:hover': { borderColor: THEME.steel, bgcolor: alpha(THEME.steel, 0.04) }
                }}
              >
                Attach files / images
                <input
                  hidden
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                  onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 5))}
                />
              </Button>
              {files.length > 0 && (
                <Typography variant="body2" sx={{ color: THEME.muted }}>
                  {files.length} new file(s) selected
                </Typography>
              )}
              {selectedPost?.attachments?.length > 0 && (
                <Typography variant="caption" sx={{ color: THEME.muted }}>
                  Existing attachments stay unless you remove the notice. New files are added on save.
                </Typography>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={() => setDialogOpen(false)}
              disabled={saving}
              sx={{ textTransform: 'none', color: THEME.muted, fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={handleFormSubmit} disabled={saving} sx={goldButtonSx}>
              {saving ? 'Saving…' : (selectedPost ? 'Save' : 'Publish')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          PaperProps={{
            sx: { borderRadius: 2.5, border: `1px solid ${THEME.border}` }
          }}
        >
          <DialogTitle sx={{ color: THEME.ink, fontWeight: 700 }}>Remove notice?</DialogTitle>
          <DialogContent>
            <Typography sx={{ color: THEME.muted }}>
              Remove “{postToDelete?.title}” from the notice board and archive? This cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              sx={{ textTransform: 'none', color: THEME.muted, fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleDeleteConfirm}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: THEME.danger,
                boxShadow: 'none',
                '&:hover': { bgcolor: alpha(THEME.danger, 0.85), boxShadow: 'none' }
              }}
            >
              Remove
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={publishersDialogOpen}
          onClose={() => !publishersSaving && setPublishersDialogOpen(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: { borderRadius: 2.5, border: `1px solid ${THEME.border}` }
          }}
        >
          <DialogTitle sx={{ color: THEME.ink, fontWeight: 700 }}>
            Who can publish
          </DialogTitle>
          <DialogContent dividers sx={{ borderColor: THEME.border }}>
            <Typography variant="body2" sx={{ color: THEME.muted, mb: 2 }}>
              Super Admin, Accountable Manager, MRO AM, and Head of Operations can always publish.
              Add specific people below who should also be allowed to publish.
            </Typography>
            {publishersLoading ? (
              <Box sx={{ py: 4, display: 'grid', placeItems: 'center' }}>
                <CircularProgress sx={{ color: THEME.steel }} />
              </Box>
            ) : (
              <Autocomplete
                multiple
                options={publisherCandidates}
                value={selectedPublishers}
                onChange={(_, value) => setSelectedPublishers(value)}
                getOptionLabel={(option) => {
                  const name = `${option.firstName || ''} ${option.lastName || ''}`.trim();
                  return name ? `${name} (${option.email || option.role || ''})` : (option.email || '');
                }}
                isOptionEqualToValue={(option, value) => String(option._id) === String(value._id)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option._id}
                      size="small"
                      label={`${option.firstName || ''} ${option.lastName || ''}`.trim() || option.email}
                      sx={{
                        fontWeight: 600,
                        bgcolor: alpha(THEME.steel, 0.1),
                        color: THEME.navy
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select people"
                    placeholder="Search by name"
                    sx={fieldSx}
                  />
                )}
              />
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={() => setPublishersDialogOpen(false)}
              disabled={publishersSaving}
              sx={{ textTransform: 'none', color: THEME.muted, fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSavePublishers}
              disabled={publishersLoading || publishersSaving}
              sx={goldButtonSx}
            >
              {publishersSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default NoticeBoard;
